import { useState, useEffect } from 'react';
import { ResultOf } from "gql.tada";
import { GetBlogHomepageById } from "@queries/archetypes/GetBlogHomepageById";
import { GetBlogFeed } from "@queries/GetBlogFeed";
import { TagFragment } from "@queries/fragments/TagFragment";
import { useContent } from "@hooks/useContent";
import { useScrollReset } from "@hooks/useScrollReset";
import { Hero } from "@components/entries/Hero";
// import { BackgroundDeepContainer } from "@components/shared/BackgroundDeepContainer";
import { NotFound } from "@components/shared/NotFound";
import { RoutingSection } from "@components/entries/RoutingSection";
import { BlogCard } from "@components/entries/BlogComponents/BlogCard";
import { Button } from "@components/shared/core/Button";
import { useTranslation } from "@services/i18n";
import { OBELIX_BLOG_KEYS } from "@utils/obelixKeys";
import { useGlobalContext } from "@utils/GlobalContext";
import { isPageAllowedForLocale } from "@utils/localeRestrictions";

type Data = NonNullable<ResultOf<typeof GetBlogHomepageById>["blogHomepage"]>;
type Tag = ResultOf<typeof TagFragment>;

export const BlogHomepageRenderer = ({ data }: { data: Data }) => {
  useScrollReset();

  const { t } = useTranslation();
  const [postTags, setPostTags] = useState<Tag[] | null>();
  const [currentTag, setCurrentTag] = useState<Tag | null>();

  const { locale } = useGlobalContext();
  const isAllowedByMarkets = isPageAllowedForLocale(locale, data?.showPageInLocales as string[] | undefined ?? []);

  if (!isAllowedByMarkets) {
    return <NotFound />;
  }

  const [response] = useContent({
    query: GetBlogFeed
  });

  const blogFeed = response?.data?.blogPostPageCollection?.items;
  const featured: any = data?.featuredStory;

  useEffect(() => {
    if (blogFeed) {
      const tags: Tag[] = [{ slug: 'all', tagLabel: 'All' }];

      blogFeed.forEach((post: any) => {
        const items = post?.tagsCollection?.items;

        if (items.length) {
          items.forEach((tag: Tag) => {
            if (!tags.find(t => t.slug === tag.slug)) {
              tags.push(tag);
            }
          });
        }
      });

      setPostTags(tags);
      setCurrentTag(tags[0]);
    }
  }, [blogFeed]);

  return (
    <div>
      {data?.hero && (
        <Hero entry={{ hero: data.hero } as any} />
      )}

      <div className="max-w-[1260px] mx-auto">
        {featured && (
          <div className="featured-blog container-base my-[64px] dsk:my-[82px]">
            <h2 className="text-heading-1 capitalize text-center text-balance my-[30px] dsk:my-[64px]">
              {t(OBELIX_BLOG_KEYS.featuredStory.obelixKey, OBELIX_BLOG_KEYS.featuredStory.label)}
            </h2>
            <BlogCard post={featured} variant="full" />
          </div>
        )}

        {(blogFeed && blogFeed.length > 0) && (
          <div className="container-base my-[64px] dsk:my-[82px]">
            <h2 className="text-heading-1 capitalize text-center text-balance my-[30px] dsk:my-[64px]">
              {t(OBELIX_BLOG_KEYS.allBlogPosts.obelixKey, OBELIX_BLOG_KEYS.allBlogPosts.label)}
            </h2>

            <div className="tags flex gap-[10px] justify-center my-[60px] max-w-[400px] mx-auto">
              {postTags?.map((tag: Tag) => (
                <Button
                  key={tag.slug}
                  className="whitespace-nowrap"
                  variant={tag.slug === currentTag?.slug ? 'light-on-dark' : 'highlight-on-deep'}
                  onClick={() => setCurrentTag(tag)}>
                  {tag.tagLabel}
                </Button>
              ))}
            </div>

            <div className="grid dsk:grid-cols-3 gap-x-[50px] gap-y-[90px] justify-center">
              {blogFeed.map((post: any) => {
                if (currentTag?.slug === 'all' || post.tagsCollection?.items.find((tag: Tag) => tag.slug === currentTag?.slug)) {
                  return (
                    <BlogCard key={post.slug} post={post} />
                  )}
                }
              )}
            </div>
          </div>
        )}
      </div>

      {data?.ctaFooter && (
        // <BackgroundDeepContainer>
          <RoutingSection entry={{ routingSection: data?.ctaFooter } as any} />
        // </BackgroundDeepContainer>
      )}
    </div>
  );
};
